import * as React from 'react';
import { compose, mapProps } from 'recompose';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { withStyles, makeStyles } from '@material-ui/core/styles';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import { toFormattedLocalDate } from 'utils/dateTime/converter';
import AssessmentProgressBar from 'components/threepp/AssessmentProgressBar/AssessmentProgressBar';
import Tooltip from 'components/common/Tooltip/Tooltip';

import './AssessmentsTable.scss';
import { getTimeRemaining } from 'utils/dateTime/DueDateCountDown';
import { Badge } from '@material-ui/core';
import { Typography } from '@mui/material';

export const StyledTableCell = withStyles(() => ({
  head: {
    padding: '10px',
    fontSize: '1rem'
  },
  body: {
    width: '200px',
    fontSize: 14,
    padding: '0px 5px 0px 10px',
    cursor: 'pointer'
  }
}))(TableCell);

export const StyledBadge = withStyles(theme => ({
  badge: {
    marginRight: '6px',
    height: '6px',
    minWidth: '6px',
    marginTop: '20px',
    [theme.breakpoints.down(theme.breakpoints.values.mobile)]: {
      marginRight: '8px',
      marginTop: '0'
    }
  }
}))(Badge);

const useStyles = makeStyles(() => ({
  ownerName: {
    paddingBottom: '15px',
    maxWidth: '180px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  name: {
    paddingBottom: '12px',
    maxWidth: '200px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginRight: '10px'
  },
  companyName: {
    paddingBottom: '15px',
    maxWidth: '150px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginRight: '10px'
  }
}));

export const dateSortMethod = assessments => {
  const sortedDateAssessments = assessments.sort((a, b) => {
    return new Date(a.dueDate) - new Date(b.dueDate);
  });
  return sortedDateAssessments;
};

export const getSubmittedDate = (submitDate, isMobile) => {
  const submittedDateDisplay = toFormattedLocalDate(submitDate);
  return isMobile ? (
    <p className="assessmentTableDate">Due: {submittedDateDisplay}</p>
  ) : (
    <StyledTableCell align="justify"> {submittedDateDisplay}</StyledTableCell>
  );
};

export const getTableHeading = (assessments, props) => {
  let tableHeads;
  assessments.forEach(({ status }) => {
    if (status === 'SUBMITTED' || status === 'SUBMITTED_SYNC_SUCCESS') {
      tableHeads = [
        props.t('model.assessment.name'),
        props.t('model.assessment.submitted'),
        props.t('model.assessment.company'),
        props.t('model.assessment.owners'),
        props.t('common.progress')
      ];
    } else {
      tableHeads = [
        props.t('model.assessment.name'),
        props.t('model.assessment.dueDate'),
        props.t('model.assessment.company'),
        props.t('model.assessment.owners'),
        props.t('common.progress')
      ];
    }
  });
  return tableHeads;
};

export const getDueDate = (row, isMobile) => {
  const daysRemaining = getTimeRemaining(row.dueDate).days;
  let dueDateDisplay = toFormattedLocalDate(row.dueDate);
  let styleObject;

  switch (true) {
    case daysRemaining <= -1:
      dueDateDisplay = `${Math.abs(daysRemaining)} days overdue`;
      styleObject = { fontWeight: 600, color: '#f44336' };
      break;

    case daysRemaining <= 6 && daysRemaining > 0:
      dueDateDisplay = daysRemaining === 1 ? `Due in ${daysRemaining} day` : `Due in ${daysRemaining} days`;
      styleObject = { fontWeight: 600, color: '#f47236' };
      break;

    case daysRemaining === 0:
      dueDateDisplay = `Due Today`;
      styleObject = { fontWeight: 600, color: '#f47236' };
      break;

    default:
      break;
  }
  return isMobile ? (
    <p style={{ ...styleObject }} className="assessmentTableDate">
      {dueDateDisplay}
    </p>
  ) : (
    <StyledTableCell style={styleObject}>{dueDateDisplay} </StyledTableCell>
  );
};

export const navToAssessment = (id, props) => {
  props.history.push({ pathname: `/assessments/${id}` });
};

export const AssessmentsTable = props => {
  const { assessments } = props;
  const classes = useStyles();
  return (
    <TableContainer className="assessments-main-table">
      <Table aria-label="customized table">
        <TableHead>
          <TableRow>
            {assessments &&
              assessments.length !== 0 &&
              getTableHeading(assessments, props).map(column => (
                <StyledTableCell key={column} className="table-head">
                  {' '}
                  {column}
                </StyledTableCell>
              ))}
          </TableRow>
        </TableHead>
        <TableBody className="table-body">
          {assessments &&
            assessments.length !== 0 &&
            dateSortMethod(assessments).map(row => {
              const owners = [];
              row &&
                row.owners.map(assessmentOwner => {
                  const { firstName, lastName } = assessmentOwner;
                  owners.push(` ${firstName} ${lastName}`);
                  return owners;
                });
              return (
                <TableRow onClick={() => navToAssessment(row.id, props)} key={row.id}>
                  <Tooltip title={row.name} placement="bottom">
                    <StyledTableCell className={classes.name} style={row.status === 'NEW' ? { fontWeight: 600 } : {}} size="small">
                      <StyledBadge key={row.id} color="primary" variant="dot" invisible={row.status !== 'NEW'} />
                      <Typography className={classes.name}>{row.name}</Typography>
                    </StyledTableCell>
                  </Tooltip>

                  {row.status === 'SUBMITTED' || row.status === 'SUBMITTED_SYNC_SUCCESS' ? getSubmittedDate(row.submitDate) : getDueDate(row)}
                  <Tooltip title={row.company} placement="bottom">
                    <StyledTableCell style={row.status === 'NEW' ? { fontWeight: 700 } : {}} size="small" align="justify">
                      <StyledBadge />
                      <Typography className={classes.companyName}>{row.company}</Typography>
                    </StyledTableCell>
                  </Tooltip>

                  <Tooltip title={owners.toString()} placement="bottom">
                    <StyledTableCell size="small">
                      <StyledBadge key={row.id} color="primary" />
                      <Typography className={classes.ownerName}>{owners.toString()}</Typography>
                    </StyledTableCell>
                  </Tooltip>

                  <StyledTableCell align="justify">
                    <AssessmentProgressBar className="col-completed" answered={row.metadata.answeredFields} total={row.metadata.totalFields} />
                  </StyledTableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const AssessmentsTableHOC = compose(
  withRouter,
  withTranslation(),
  mapProps(props => ({
    t: props.t,
    history: props.history,
    assessments: props.assessments
  }))
);

export default AssessmentsTableHOC(AssessmentsTable);
